import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subscription, filter } from 'rxjs';

import { TravelBoardService } from '../../services/travel-board.service';
import { TripCreationModalService } from 'src/app/trip-calculator/services/trip-creation-modal.service';
import { BrochureAnalysisService } from 'src/app/trip-calculator/services/brochure-analysis.service';
import { BulkBrochureAnalysisComponent } from 'src/app/trip-calculator/components/bulk-brochure-analysis/bulk-brochure-analysis.component';
import { BatchUploadService } from 'src/app/trip-calculator/services/batch-upload.service';
import {
  PageHeaderAction,
  PageHeaderComponent,
} from '../../../shared/components/page-header/page-header.component';
import { BoardTripListSectionComponent } from '../board-trip-list-section/board-trip-list-section.component';
import { ExportTripsToXlsComponent } from '../export-trips-to-xls/export-trips-to-xls.component';
import { ExportCompanyReportToPdfComponent } from '../export-company-report-to-pdf/export-company-report-to-pdf.component';
import { TripCreationModalComponent } from 'src/app/trip-calculator/components/trip-creation-modal/trip-creation-modal/trip-creation-modal.component';
import { CollapsibleSectionComponent } from 'src/app/shared/components/collapsible-section/collapsible-section.component';
import { BoardOverviewSectionComponent } from '../board-overview-section/board-overview-section.component';
import { EmissionPerTripChartComponent } from '../emission-per-trip-chart/emission-per-trip-chart.component';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { EmptyViewComponent } from '../empty-view/empty-view.component';
import { BrochureAnalysisModalComponent } from 'src/app/trip-calculator/components/brochure-analysis-modal/brochure-analysis-modal.component';
import { ExportModalComponent } from '../export-modal/export-modal.component';
import { TransferTripDialogComponent } from '../transfer-trip-dialog/transfer-trip-dialog.component';
import { GreentripperLoaderComponent } from 'src/app/shared/components/greentripper-loader/greentripper-loader.component';

@Component({
  selector: 'app-board',
  imports: [
    BoardTripListSectionComponent,
    PageHeaderComponent,
    ExportTripsToXlsComponent,
    ExportCompanyReportToPdfComponent,
    TripCreationModalComponent,
    CollapsibleSectionComponent,
    BoardOverviewSectionComponent,
    EmissionPerTripChartComponent,
    TranslocoPipe,
    ConfirmDialog,
    EmptyViewComponent,
    BrochureAnalysisModalComponent,
    BulkBrochureAnalysisComponent,
    ExportModalComponent,
    TransferTripDialogComponent,
    GreentripperLoaderComponent,
    DialogModule,
  ],
  templateUrl: './board.component.html',
  styleUrl: './board.component.scss',
})
export class BoardComponent implements OnInit, OnDestroy {
  travelBoardService = inject(TravelBoardService);
  tripCreationModalService = inject(TripCreationModalService);
  brochureAnalysisService = inject(BrochureAnalysisService);
  private batchUploadService = inject(BatchUploadService);

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private translocoService = inject(TranslocoService);

  private languageChangeSubscription: Subscription | undefined;
  private routerSubscription: Subscription | undefined;
  private previousUrlPath: string = '';

  showPage = true;
  showExportModal = false;

  headerActions: PageHeaderAction[] = [
    {
      label: 'travelBoards.addNewTrip',
      icon: 'plus',
      variant: 'primary',
      onClick: () => this.tripCreationModalService.createTrip(),
      testId: 'add-trip-button',
    },
  ];

  ngOnInit(): void {
    this.travelBoardService.getTrips();

    this.languageChangeSubscription =
      this.translocoService.langChanges$.subscribe(() => {
        this.travelBoardService.reloadData();
      });

    this.initializeFromQueryParams();
    this.setupRouterSubscription();
  }

  ngOnDestroy(): void {
    this.languageChangeSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    // Navigating away unmounts the modal: same handling as an explicit close.
    // An active upload keeps running under the floating widget; a terminal
    // (done/error) or idle state is cleared here so the widget's own effect
    // doesn't re-surface an outcome toast the modal already showed.
    this.onBulkModalClose();
  }

  // Query params handlers
  onSearchChange(search: string): void {
    this.updateQueryParams({ search: search || undefined, page: 0 });
  }

  onFilterChange(filter: 'all' | 'draft' | 'notDraft'): void {
    const statusMap: Record<string, string | undefined> = {
      draft: 'draft',
      notDraft: 'active',
      all: undefined,
    };
    this.updateQueryParams({ status: statusMap[filter], page: 0 });
  }

  onPaginationChange(event: { page: number; rows: number }): void {
    this.travelBoardService.paginationPage.set(event.page);
    this.travelBoardService.paginationRows.set(event.rows);
    this.updateQueryParams({ page: event.page, rows: event.rows });
  }

  private initializeFromQueryParams(): void {
    this.route.queryParams.subscribe((params) => {
      const statusParam = params['status'];
      if (statusParam === 'draft') {
        this.travelBoardService.isDraftFilter.set('draft');
      } else if (statusParam === 'active') {
        this.travelBoardService.isDraftFilter.set('notDraft');
      } else {
        this.travelBoardService.isDraftFilter.set('all');
      }

      const page = parseInt(params['page'], 10);
      if (!isNaN(page) && page >= 0) {
        this.travelBoardService.paginationPage.set(page);
      }

      const rows = parseInt(params['rows'], 10);
      const validRowsOptions = [5, 10, 12, 24, 25, 48, 50];
      if (!isNaN(rows) && validRowsOptions.includes(rows)) {
        this.travelBoardService.paginationRows.set(rows);
      }

      const search = params['search'];
      if (search !== undefined) {
        this.travelBoardService.searchText.set(search);
      }
    });
  }

  private setupRouterSubscription(): void {
    this.previousUrlPath = this.router.url.split('?')[0];
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navEvent = event as NavigationEnd;
        const currentPath = navEvent.urlAfterRedirects.split('?')[0];

        if (
          currentPath.includes('/travel-board') &&
          this.previousUrlPath !== currentPath
        ) {
          this.travelBoardService.getTrips();
        }

        this.previousUrlPath = currentPath;
      });
  }

  private updateQueryParams(params: {
    page?: number;
    rows?: number;
    search?: string | undefined;
    status?: string | undefined;
  }): void {
    const currentParams = new URLSearchParams(window.location.search);

    if (params.page !== undefined) {
      if (params.page === 0) {
        currentParams.delete('page');
      } else {
        currentParams.set('page', params.page.toString());
      }
    }
    if (params.rows !== undefined) {
      currentParams.set('rows', params.rows.toString());
    }
    if ('search' in params) {
      if (params.search) {
        currentParams.set('search', params.search);
      } else {
        currentParams.delete('search');
      }
    }
    if ('status' in params) {
      if (params.status) {
        currentParams.set('status', params.status);
      } else {
        currentParams.delete('status');
      }
    }

    const queryString = currentParams.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    this.location.replaceState(newUrl);
  }

  onBulkModalClose(): void {
    this.brochureAnalysisService.showBulkUploadModal.set(false);
    // Closing while an upload is actively running must NOT wipe it — the global
    // progress widget takes over (minimized view). For any non-active status
    // (idle queue, or a terminal done/error already shown), clear the state.
    const status = this.batchUploadService.state().status;
    const isActive =
      status === 'initializing' ||
      status === 'uploading' ||
      status === 'finalizing';
    if (!isActive) {
      this.batchUploadService.reset();
    }
  }
}
